import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import CatalogueProductCard from '../components/directory/CatalogueProductCard';
import '../components/TableSkeleton.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const PRODUCTS_PER_PAGE = 50;
const SIDEBAR_SUPPLIERS_LIMIT = 200;
// Past this many suppliers in the rail, a filter box is worth the space.
const SIDEBAR_FILTER_THRESHOLD = 10;

function getInitials(name) {
  return String(name || '').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function supplierCoverage(s) {
  const total = s?.product_count || 0;
  const assessed = Math.min(s?.completed_assessment_count || 0, total);
  return { total, assessed, missing: Math.max(0, total - assessed) };
}

export default function SupplierCatalogue() {
  const { supplierId } = useParams();
  const { supplierInfo, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [allSuppliers, setAllSuppliers] = useState([]);
  const [totalSupplierCount, setTotalSupplierCount] = useState(0);
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Categories used by this supplier (nested with subcategories).
  // Empty buckets are removed by the API.
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedFootprint, setSelectedFootprint] = useState(''); // '' | 'true' | 'false'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingPull, setPendingPull] = useState(false);
  const [pcfRequestProduct, setPcfRequestProduct] = useState(null); // product being requested in modal
  const [pcfQuantity, setPcfQuantity] = useState('');
  const [pcfReason, setPcfReason] = useState('');
  const [pcfAddToCatalogue, setPcfAddToCatalogue] = useState(true);
  const [pcfSubmitting, setPcfSubmitting] = useState(false);
  const [pcfError, setPcfError] = useState('');
  const [pcfSuccess, setPcfSuccess] = useState('');
  // Bulk pull ("pull everything matching") — counts are previewed before the
  // distributor commits, since one click can add thousands of products.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null); // { filtered, catalogue }
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkScope, setBulkScope] = useState('catalogue'); // 'catalogue' | 'filtered'
  const [bulkOnlyPcf, setBulkOnlyPcf] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  // Bulk PCF request ("ask this supplier for footprints across the catalogue"),
  // the counterpart to bulk pull for the suppliers that have no data yet.
  const [askOpen, setAskOpen] = useState(false);
  const [askPreview, setAskPreview] = useState(null);
  const [askPreviewLoading, setAskPreviewLoading] = useState(false);
  const [askReason, setAskReason] = useState('');
  const [askSubmitting, setAskSubmitting] = useState(false);
  const [askError, setAskError] = useState('');
  const [askResult, setAskResult] = useState(null);
  // Sidebar: the catalogue-only suppliers are collapsed by default, and the
  // filter box only appears once the rail is long enough to need one.
  const [sidebarQuery, setSidebarQuery] = useState('');
  const [showCatalogueOnly, setShowCatalogueOnly] = useState(false);
  // The footprint filter defaults per supplier (see the effect below), so the
  // product fetch waits until that decision is made rather than firing twice.
  const [footprintReady, setFootprintReady] = useState(false);
  const debounceTimer = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (authLoading) return;
    if (supplierInfo && supplierInfo.account_type !== 'Distributor') {
      navigate('/products', { replace: true });
    }
  }, [authLoading, supplierInfo, navigate]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/distributor/directory`, {
      params: { page: 1, items_per_page: SIDEBAR_SUPPLIERS_LIMIT },
    }).then(res => {
      setAllSuppliers(res.data.data || []);
      setTotalSupplierCount(res.data.count || 0);
    }).catch(() => {
      // Without the rail's coverage numbers there's nothing to base the
      // footprint default on, so fall through to showing everything.
      setFootprintReady(true);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/distributor/directory/${supplierId}/categories`)
      .then(res => { if (!cancelled) setCategories(res.data.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [supplierId]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 350);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    if (!footprintReady) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`${API_BASE_URL}/distributor/directory/${supplierId}/catalogue`, {
      params: {
        page,
        items_per_page: PRODUCTS_PER_PAGE,
        search: searchQuery,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        has_footprint: selectedFootprint,
      },
    }).then(res => {
      if (cancelled) return;
      setSupplier(res.data.supplier);
      setProducts(res.data.data || []);
      setTotalCount(res.data.count || 0);
    }).catch(err => {
      if (cancelled) return;
      setError(err.response?.data?.error || 'Failed to load catalogue');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [supplierId, page, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, refreshKey, footprintReady]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSearchInput('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedFootprint('');
    setFootprintReady(false);
    setPage(1);
  }, [supplierId]);

  // A catalogue opens on its footprints. Ordering products by name buries 352
  // assessed products in 837, and the first screen of a partly-assessed
  // catalogue was almost entirely "No PCF" cards; the assessed products are
  // what a distributor came for, so they're the default view. Suppliers with
  // nothing assessed open on their full catalogue instead of an empty grid.
  useEffect(() => {
    if (footprintReady || allSuppliers.length === 0) return;
    const active = allSuppliers.find(s => s.id === supplierId);
    if (active && supplierCoverage(active).assessed > 0) setSelectedFootprint('true');
    setFootprintReady(true);
  }, [allSuppliers, supplierId, footprintReady]);

  const activeSupplier = useMemo(
    () => allSuppliers.find(s => s.id === supplierId) || null,
    [allSuppliers, supplierId]
  );
  const coverage = supplierCoverage(activeSupplier);

  // The rail is already ranked by coverage server-side; the split just keeps
  // the suppliers with no footprints from filling the visible list.
  const sidebarMatches = useMemo(() => {
    const term = sidebarQuery.trim().toLowerCase();
    if (!term) return allSuppliers;
    return allSuppliers.filter(s => String(s.name || '').toLowerCase().includes(term));
  }, [allSuppliers, sidebarQuery]);
  const railWithFootprints = useMemo(
    () => sidebarMatches.filter(s => supplierCoverage(s).assessed > 0),
    [sidebarMatches]
  );
  const railCatalogueOnly = useMemo(
    () => sidebarMatches.filter(s => supplierCoverage(s).assessed === 0),
    [sidebarMatches]
  );

  // Browsing a catalogue-only supplier has to show it in the rail, so the
  // collapsed group opens itself when the active supplier is inside it.
  useEffect(() => {
    if (railCatalogueOnly.some(s => s.id === supplierId)) setShowCatalogueOnly(true);
  }, [railCatalogueOnly, supplierId]);

  // Deep links from the directory: ?request=<id> opens the single-product
  // request (the name rides along so the modal needn't refetch the product),
  // ?request_pcfs=1 opens the whole-catalogue request.
  useEffect(() => {
    const requestId = searchParams.get('request');
    const wantsBulk = searchParams.get('request_pcfs');
    if (!requestId && !wantsBulk) return;
    if (requestId) {
      openPcfRequestModal({ id: requestId, name: searchParams.get('request_name') || 'this product' });
    } else {
      openAskModal();
    }
    const next = new URLSearchParams(searchParams);
    ['request', 'request_name', 'request_pcfs'].forEach(k => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleSelect = (productId, isLinked) => {
    if (isLinked) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const openPcfRequestModal = (product) => {
    setPcfRequestProduct(product);
    setPcfQuantity('');
    setPcfReason('');
    setPcfAddToCatalogue(true);
    setPcfError('');
    setPcfSuccess('');
  };

  const closePcfRequestModal = () => {
    if (pcfSubmitting) return;
    setPcfRequestProduct(null);
    setPcfError('');
    setPcfSuccess('');
  };

  const submitPcfRequest = async (e) => {
    e.preventDefault();
    if (!pcfRequestProduct || pcfSubmitting) return;
    const qty = parseInt(pcfQuantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      setPcfError('Enter a quantity (whole number greater than 0).');
      return;
    }
    if (!pcfReason.trim()) {
      setPcfError('Add a short reason so the supplier knows the context.');
      return;
    }
    setPcfSubmitting(true);
    setPcfError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/distributor/pcf-requests`, {
        source_product_id: pcfRequestProduct.id,
        quantity: qty,
        reason: pcfReason.trim(),
      });
      const requestedId = pcfRequestProduct.id;

      // Pull the product into the distributor's catalogue so the PCF is ready
      // to use the moment the supplier completes the assessment. Best-effort:
      // a failure here shouldn't fail the request the supplier already received.
      let addedToCatalogue = false;
      if (pcfAddToCatalogue && !pcfRequestProduct.already_linked) {
        try {
          await axios.post(`${API_BASE_URL}/distributor/links`, { source_product_id: requestedId });
          addedToCatalogue = true;
        } catch (linkErr) {
          // already_linked elsewhere or transient — leave catalogue state untouched.
        }
      }

      setProducts(prev =>
        prev.map(p => p.id === requestedId
          ? { ...p, already_requested: true, already_linked: p.already_linked || addedToCatalogue }
          : p
        )
      );

      const catalogueNote = addedToCatalogue
        ? ` We've added it to your catalogue, so the PCF will be ready there as soon as it's complete.`
        : '';
      setPcfSuccess(
        (res.data?.email_sent
          ? `Request sent to ${supplier?.name || 'the supplier'}. They will reply directly to you.`
          : res.data?.supplier_has_email === false
            ? `Request saved. ${supplier?.name || 'This supplier'} hasn't claimed their Zilch account yet, so we couldn't email them — we'll surface it next time they sign in.`
            : `Request saved. We'll try the email again shortly.`)
        + catalogueNote
      );
    } catch (err) {
      setPcfError(err.response?.data?.error || 'Failed to send request');
    } finally {
      setPcfSubmitting(false);
    }
  };

  // Pulling never consumes the distributor's assessment allowance, so there's
  // no tier limit to trip — the whole selection goes in one request.
  const handleBulkPull = async () => {
    if (selectedIds.size === 0 || pendingPull) return;
    setPendingPull(true);
    setError('');
    const ids = Array.from(selectedIds);
    try {
      const res = await axios.post(`${API_BASE_URL}/distributor/links/bulk`, {
        supplier_id: supplierId,
        product_ids: ids,
      });
      if (res.data?.skipped_invalid > 0) {
        // Something in the selection is no longer pullable (deactivated, moved).
        // Don't mark cards optimistically — reload the page's real state.
        setError(`${res.data.skipped_invalid} of the selected products are no longer available from this supplier.`);
        setRefreshKey(k => k + 1);
      } else {
        setProducts(prev => prev.map(p => (selectedIds.has(p.id) ? { ...p, already_linked: true } : p)));
      }
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to pull products');
      // A partial failure can still have linked some of the batch — resync.
      setRefreshKey(k => k + 1);
    } finally {
      setPendingPull(false);
    }
  };

  const loadBulkPreview = async (withFilters) => {
    setBulkPreviewLoading(true);
    setBulkError('');
    setBulkPreview(null);
    try {
      const url = `${API_BASE_URL}/distributor/directory/${supplierId}/pull-preview`;
      // Two scopes: what the current filters cover, and the whole catalogue.
      // The footprint checkbox isn't sent — "only products with a PCF" is a
      // choice inside the modal, so both counts stay available.
      const [filteredRes, catalogueRes] = await Promise.all([
        withFilters
          ? axios.get(url, {
              params: {
                search: searchQuery,
                category: selectedCategory,
                subcategory: selectedSubcategory,
              },
            })
          : null,
        axios.get(url),
      ]);
      setBulkPreview({
        filtered: filteredRes ? filteredRes.data : catalogueRes.data,
        catalogue: catalogueRes.data,
      });
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Failed to count products');
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const openBulkModal = () => {
    const withFilters = !!(searchQuery || selectedCategory || selectedSubcategory);
    setBulkOpen(true);
    setBulkResult(null);
    setBulkError('');
    setBulkScope(withFilters ? 'filtered' : 'catalogue');
    setBulkOnlyPcf(selectedFootprint === 'true');
    loadBulkPreview(withFilters);
  };

  const closeBulkModal = () => {
    if (bulkSubmitting) return;
    setBulkOpen(false);
    setBulkResult(null);
    setBulkError('');
  };

  const submitBulkPull = async () => {
    if (bulkSubmitting) return;
    setBulkSubmitting(true);
    setBulkError('');
    try {
      const body = { supplier_id: supplierId, only_with_footprint: bulkOnlyPcf };
      if (bulkScope === 'filtered') {
        body.search = searchQuery;
        body.category = selectedCategory;
        body.subcategory = selectedSubcategory;
      }
      const res = await axios.post(`${API_BASE_URL}/distributor/links/bulk`, body);
      setBulkResult(res.data);
      setSelectedIds(new Set());
      setRefreshKey(k => k + 1);
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Failed to pull products');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const pcfCount = products.filter(p => p.has_assessment).length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PRODUCTS_PER_PAGE));

  const hasActiveFilters = !!(searchQuery || selectedCategory || selectedSubcategory);
  const activeCategory = categories.find(c => c.id === selectedCategory) || null;
  const activeSubcategoryName =
    (activeCategory?.subcategories || []).find(s => s.id === selectedSubcategory)?.name || '';
  const filterScopeLabel = [
    activeSubcategoryName
      ? `${activeCategory?.name || 'Category'} › ${activeSubcategoryName}`
      : (activeCategory?.name || ''),
    searchQuery ? `matching “${searchQuery}”` : '',
  ].filter(Boolean).join(' · ') || 'Your current filters';

  const bulkScopePreview = bulkPreview
    ? (bulkScope === 'catalogue' ? bulkPreview.catalogue : bulkPreview.filtered)
    : null;
  const bulkNewCount = bulkScopePreview
    ? (bulkOnlyPcf ? bulkScopePreview.new_with_footprint : bulkScopePreview.new_total)
    : 0;
  const bulkScopeTotal = bulkScopePreview
    ? (bulkOnlyPcf ? bulkScopePreview.with_footprint : bulkScopePreview.total)
    : 0;

  // "N to add · M already yours" for one scope. Shared by the scope radios and
  // the single-scope summary so the two can never disagree. Reflects the
  // only-with-PCF checkbox, which is why it lives above that control.
  // ── Bulk PCF request ───────────────────────────────────────────────
  // A distributor staring at 3,498 products with no footprints needs one
  // action, not 3,498. The request follows the filters they're looking at, so
  // "ask for footprints" means the category on screen, not blindly everything.
  const askScopeLabel = () => {
    if (selectedSubcategory) {
      const subs = categories.find(c => c.id === selectedCategory)?.subcategories || [];
      const sub = subs.find(s => s.id === selectedSubcategory);
      if (sub) return sub.name;
    }
    if (selectedCategory) {
      const cat = categories.find(c => c.id === selectedCategory);
      if (cat) return cat.name;
    }
    if (searchQuery) return `products matching "${searchQuery}"`;
    return 'the whole catalogue';
  };

  const loadAskPreview = async () => {
    setAskPreviewLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/distributor/directory/${supplierId}/pcf-request-preview`, {
        params: { search: searchQuery, category: selectedCategory, subcategory: selectedSubcategory },
      });
      setAskPreview(res.data);
    } catch (err) {
      setAskError(err.response?.data?.error || 'Could not work out how many products to request.');
    } finally {
      setAskPreviewLoading(false);
    }
  };

  const openAskModal = () => {
    setAskOpen(true);
    setAskReason('');
    setAskError('');
    setAskResult(null);
    setAskPreview(null);
    loadAskPreview();
  };

  const closeAskModal = () => {
    setAskOpen(false);
    setAskResult(null);
    setAskError('');
  };

  const submitAsk = async (e) => {
    e.preventDefault();
    if (askSubmitting) return;
    if (!askReason.trim()) {
      setAskError('Add a short reason so the supplier knows the context.');
      return;
    }
    setAskSubmitting(true);
    setAskError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/distributor/pcf-requests/bulk`, {
        supplier_id: supplierId,
        search: searchQuery,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        reason: askReason.trim(),
      });
      setAskResult(res.data);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setAskError(err.response?.data?.error || 'Failed to send the requests');
    } finally {
      setAskSubmitting(false);
    }
  };

  const renderScopeCounts = (preview) => {
    if (preview === null) return '…';
    const newCount = bulkOnlyPcf ? preview.new_with_footprint : preview.new_total;
    const total = bulkOnlyPcf ? preview.with_footprint : preview.total;
    if (newCount === 0) {
      return total === 0
        ? 'No matching products'
        : `All ${total.toLocaleString()} already in your catalogue`;
    }
    return (
      <>
        <strong style={{ color: '#06a457' }}>{newCount.toLocaleString()} to add</strong>
        {total > newCount && ` · ${(total - newCount).toLocaleString()} already yours`}
      </>
    );
  };

  // One scope card in the bulk-pull modal. Only rendered when there is a real
  // choice to make (filters are on) — a lone radio next to the only-with-PCF
  // checkbox reads as if both were being ticked.
  const renderScopeOption = (key, title, subtitle, preview) => {
    const isActive = bulkScope === key;
    return (
      <label
        key={key}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '12px 14px',
          marginBottom: 8,
          background: isActive ? '#f4f6f5' : '#ffffff',
          border: isActive ? '1.5px solid #c3cdc7' : '1.5px solid #e8f0eb',
          borderRadius: 6,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <input
          type="radio"
          name="bulk-scope"
          checked={isActive}
          onChange={() => setBulkScope(key)}
          style={{ cursor: 'pointer', marginTop: 3 }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#052b16' }}>{title}</span>
          <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2 }}>{subtitle}</span>
          <span style={{ display: 'block', fontSize: 12, color: '#052b16', marginTop: 5 }}>
            {renderScopeCounts(preview)}
          </span>
        </span>
      </label>
    );
  };

  // No filters on → there is only one possible scope, so state it instead of
  // offering a one-option radio group.
  const renderScopeSummary = (title, subtitle, preview) => (
    <div style={{
      padding: '12px 14px',
      background: '#f9fafa',
      border: '1px solid #e8f0eb',
      borderRadius: 6,
    }}>
      <div style={{
        fontSize: 10,
        fontWeight: 600,
        color: '#9ca3af',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}>
        Pulling
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#052b16', marginTop: 5 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{subtitle}</div>
      <div style={{ fontSize: 12, color: '#052b16', marginTop: 5 }}>
        {renderScopeCounts(preview)}
      </div>
    </div>
  );

  // One supplier in the rail. A supplier with footprints leads with the
  // assessed/total ratio and its coverage bar; a supplier without leads with
  // its size. No empty progress bar is ever drawn — a column of them was the
  // single worst thing about this page, reading as "Zilch has no data" when it
  // actually means "nobody has asked this supplier yet".
  const renderSupplierRow = (s) => {
    const isActive = s.id === supplierId;
    const { total, assessed } = supplierCoverage(s);
    const hasFootprints = assessed > 0;
    const pct = total > 0 ? Math.max(2, Math.round((assessed / total) * 100)) : 0;

    return (
      <button
        key={s.id}
        onClick={() => navigate(`/suppliers/${s.id}/catalogue`)}
        title={hasFootprints
          ? `${assessed.toLocaleString()} of ${total.toLocaleString()} products assessed`
          : `${total.toLocaleString()} products, none assessed yet`}
        style={{
          display: 'block',
          padding: '8px 10px',
          marginBottom: 3,
          border: isActive ? '1.5px solid #06a457' : '1.5px solid transparent',
          borderRadius: 6,
          width: '100%',
          background: isActive ? 'rgba(6,164,87,0.06)' : 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background 0.12s, border-color 0.12s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34,
            height: 34,
            borderRadius: 99,
            background: s.logo_url ? '#ffffff' : (isActive ? '#06a457' : '#1a2e20'),
            border: s.logo_url ? '1px solid #e8f0eb' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: 12,
            flexShrink: 0,
            letterSpacing: '0.03em',
            overflow: 'hidden',
          }}>
            {s.logo_url
              ? <img src={s.logo_url} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
              : getInitials(s.name)
            }
          </div>
          <div style={{
            minWidth: 0,
            flex: 1,
            fontWeight: 600,
            fontSize: 13,
            color: '#052b16',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {s.name}
          </div>
          {isActive && (
            <span style={{
              fontSize: 9,
              fontWeight: 700,
              color: '#06a457',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>
              Browsing
            </span>
          )}
        </div>

        <div style={{ marginLeft: 44, marginTop: 4 }}>
          <div style={{
            fontSize: 11.5,
            lineHeight: 1.2,
            color: '#9ca3af',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {hasFootprints ? (
              <>
                <span style={{ fontWeight: 700, color: '#06a457' }}>{assessed.toLocaleString()}</span>
                {' of '}{total.toLocaleString()} assessed
              </>
            ) : (
              <>{total.toLocaleString()} products · none assessed</>
            )}
          </div>
          {hasFootprints && (
            <div style={{
              marginTop: 4,
              height: 3,
              borderRadius: 2,
              background: '#e8f0eb',
              overflow: 'hidden',
            }}>
              <div style={{ width: `${pct}%`, height: '100%', background: '#06a457', borderRadius: 2 }} />
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 59px)',
      overflow: 'hidden',
      fontFamily: "'Raleway', 'SF Pro Display', sans-serif",
      background: '#ffffff',
    }}>

      {/* ── Sidebar ────────────────────────────────────────────────── */}
      <div style={{
        width: 268,
        flexShrink: 0,
        borderRight: '1px solid #e8f0eb',
        overflowY: 'auto',
        background: '#fafcfb',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 14px 20px' }}>
          <button
            type="button"
            onClick={() => navigate('/suppliers')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: 'none',
              padding: 0,
              marginBottom: 16,
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: "'Raleway', sans-serif",
              color: '#06a457',
              cursor: 'pointer',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M7.5 2L3.5 6l4 4" stroke="#06a457" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Directory &amp; search
          </button>

          {allSuppliers.length > SIDEBAR_FILTER_THRESHOLD && (
            <input
              type="text"
              value={sidebarQuery}
              onChange={e => setSidebarQuery(e.target.value)}
              placeholder="Filter suppliers…"
              style={{
                width: '100%',
                padding: '6px 10px',
                marginBottom: 14,
                fontSize: 12.5,
                border: '1px solid #d4ede2',
                borderRadius: 5,
                outline: 'none',
                fontFamily: "'Raleway', sans-serif",
                color: '#052b16',
                background: '#ffffff',
                boxSizing: 'border-box',
              }}
            />
          )}

          {railWithFootprints.length > 0 && (
            <div style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#9ca3af',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}>
              Footprint-ready
            </div>
          )}

          {railWithFootprints.map(s => renderSupplierRow(s))}

          {railCatalogueOnly.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowCatalogueOnly(v => !v)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  marginTop: railWithFootprints.length > 0 ? 18 : 0,
                  marginBottom: 10,
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#9ca3af',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontFamily: "'Raleway', sans-serif",
                  textAlign: 'left',
                }}
              >
                <svg
                  width="9" height="9" viewBox="0 0 12 12" fill="none"
                  style={{ transform: showCatalogueOnly ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}
                >
                  <path d="M4 2l4 4-4 4" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Catalogue only ({railCatalogueOnly.length})
              </button>
              {showCatalogueOnly && railCatalogueOnly.map(s => renderSupplierRow(s))}
            </>
          )}

          {sidebarMatches.length === 0 && (
            <div style={{ fontSize: 12, color: '#9ca3af', padding: '4px 2px' }}>
              No supplier matches “{sidebarQuery}”.
            </div>
          )}

          {totalSupplierCount > allSuppliers.length && (
            <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 10px', marginTop: 4 }}>
              + {(totalSupplierCount - allSuppliers.length).toLocaleString()} more connected
            </div>
          )}
        </div>
      </div>


      {/* ── Right panel ─────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{
          padding: '18px 28px',
          borderBottom: '1px solid #e8f0eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#9ca3af',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              Catalog
            </div>
            <h1 style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 300,
              color: '#052b16',
              letterSpacing: '-0.26px',
              lineHeight: 1.1,
            }}>
              {supplier?.name || '…'}
            </h1>
            {activeSupplier && coverage.total > 0 && (
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 5 }}>
                {coverage.assessed > 0 ? (
                  <>
                    <strong style={{ color: '#06a457', fontWeight: 700 }}>{coverage.assessed.toLocaleString()}</strong>
                    {' of '}{coverage.total.toLocaleString()} products carry a verified footprint
                  </>
                ) : (
                  <>{coverage.total.toLocaleString()} products · no footprints computed yet</>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {coverage.missing > 0 && (
              <button
                onClick={openAskModal}
                title="Ask this supplier to compute footprints across the products you're looking at"
                style={{
                  background: '#ffffff',
                  color: '#000000',
                  border: '1.5px solid #000000',
                  borderRadius: 6,
                  padding: '8px 16px',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: "'Raleway', sans-serif",
                  letterSpacing: '0.03em',
                  whiteSpace: 'nowrap',
                }}
              >
                Ask for footprints…
              </button>
            )}
            <button
              onClick={openBulkModal}
              disabled={pendingPull}
              title="Pull this whole catalogue, a whole category, or just the products with a PCF"
              style={{
                background: '#ffffff',
                color: '#06a457',
                border: '1.5px solid #06a457',
                borderRadius: 6,
                padding: '8px 16px',
                fontWeight: 600,
                fontSize: 13,
                cursor: pendingPull ? 'default' : 'pointer',
                fontFamily: "'Raleway', sans-serif",
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap',
              }}
            >
              {hasActiveFilters ? 'Pull all matching…' : 'Pull entire catalogue…'}
            </button>
            <button
              onClick={handleBulkPull}
              disabled={selectedIds.size === 0 || pendingPull}
              style={{
                background: selectedIds.size > 0 ? '#06a457' : '#d4ede2',
                color: selectedIds.size > 0 ? '#ffffff' : '#9ca3af',
                border: 'none',
                borderRadius: 6,
                padding: '9px 18px',
                fontWeight: 600,
                fontSize: 13,
                cursor: selectedIds.size > 0 && !pendingPull ? 'pointer' : 'default',
                fontFamily: "'Raleway', sans-serif",
                letterSpacing: '0.03em',
                transition: 'background 0.15s, color 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              {pendingPull
                ? 'Pulling…'
                : selectedIds.size > 0
                  ? `Pull ${selectedIds.size} Product${selectedIds.size !== 1 ? 's' : ''} →`
                  : 'Pull Selected →'
              }
            </button>
          </div>
        </div>

        {/* Search + category filter */}
        <div style={{ padding: '10px 28px', borderBottom: '1px solid #e8f0eb', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search products…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{
              width: '100%',
              maxWidth: 280,
              padding: '7px 12px',
              fontSize: 13,
              border: '1px solid #d4ede2',
              borderRadius: 4,
              outline: 'none',
              fontFamily: "'Raleway', sans-serif",
              color: '#052b16',
              background: '#ffffff',
            }}
          />

          

          {categories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={e => { setSelectedCategory(e.target.value); setSelectedSubcategory(''); setPage(1); }}
              style={{
                padding: '7px 12px',
                fontSize: 13,
                border: '1px solid #d4ede2',
                borderRadius: 4,
                outline: 'none',
                fontFamily: "'Raleway', sans-serif",
                color: selectedCategory ? '#052b16' : '#9ca3af',
                background: '#ffffff',
                maxWidth: 200,
              }}
            >
              <option value="">All categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          )}
          {selectedCategory && (() => {
            const subs = categories.find(c => c.id === selectedCategory)?.subcategories || [];
            if (!subs.length) return null;
            return (
              <select
                value={selectedSubcategory}
                onChange={e => { setSelectedSubcategory(e.target.value); setPage(1); }}
                style={{
                  padding: '7px 12px',
                  fontSize: 13,
                  border: '1px solid #d4ede2',
                  borderRadius: 4,
                  outline: 'none',
                  fontFamily: "'Raleway', sans-serif",
                  color: selectedSubcategory ? '#052b16' : '#9ca3af',
                  background: '#ffffff',
                  maxWidth: 200,
                }}
              >
                <option value="">All subcategories</option>
                {subs.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            );
          })()}
          {/* Footprint view. A trailing checkbox left the footprints opt-in and
              nobody found them; as a named view with its count on the label,
              the data leads. Suppliers with nothing assessed get no "with
              footprints" tab to click into an empty grid — they get the
              request banner below instead. */}
          {coverage.assessed > 0 && (
            <div style={{
              display: 'inline-flex',
              border: '1px solid #d4ede2',
              borderRadius: 5,
              overflow: 'hidden',
              marginLeft: 'auto',
              flexShrink: 0,
            }}>
              {[
                { value: 'true', label: 'With footprints', count: coverage.assessed },
                { value: 'false', label: 'Needs a footprint', count: coverage.missing },
                { value: '', label: 'All', count: coverage.total },
              ].map(opt => (
                <button
                  key={opt.value || 'all'}
                  type="button"
                  onClick={() => { setSelectedFootprint(opt.value); setPage(1); }}
                  style={{
                    padding: '7px 12px',
                    fontSize: 12.5,
                    fontWeight: 600,
                    fontFamily: "'Raleway', sans-serif",
                    letterSpacing: '0.02em',
                    border: 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    background: selectedFootprint === opt.value ? '#06a457' : '#ffffff',
                    color: selectedFootprint === opt.value ? '#ffffff' : '#4a5568',
                  }}
                >
                  {opt.label}
                  <span style={{
                    marginLeft: 5,
                    fontWeight: 500,
                    color: selectedFootprint === opt.value ? 'rgba(255,255,255,0.75)' : '#9ca3af',
                  }}>
                    {opt.count.toLocaleString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* A catalogue with no footprints is a request waiting to happen, not
            an empty shelf — say so, and put the action next to the sentence. */}
        {coverage.total > 0 && coverage.assessed === 0 && (
          <div style={{
            margin: '12px 28px 0',
            padding: '13px 16px',
            background: '#fafcfb',
            border: '1px solid #e8f0eb',
            borderRadius: 7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexShrink: 0,
          }}>
            <div style={{ fontSize: 13, color: '#052b16', lineHeight: 1.5 }}>
              <strong style={{ fontWeight: 700 }}>{supplier?.name || 'This supplier'} hasn't computed any footprints yet.</strong>
              <span style={{ color: '#6b7280' }}>
                {' '}You can still pull their products into your catalogue — and telling them which
                ones you sell is the fastest way to get the data.
              </span>
            </div>
            <button
              type="button"
              onClick={openAskModal}
              style={{
                background: '#000000',
                color: '#ffffff',
                border: 'none',
                borderRadius: 6,
                padding: '9px 16px',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: "'Raleway', sans-serif",
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Request footprints
            </button>
          </div>
        )}

        {error && (
          <div style={{
            margin: '10px 28px 0',
            padding: '8px 12px',
            background: '#fff5f5',
            border: '1px solid #fca5a5',
            borderRadius: 4,
            color: '#dc2626',
            fontSize: 13,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}>
            <span>{error}</span>
          </div>
        )}

        {/* Product grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ border: '1.5px solid #e8f0eb', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{
                    paddingBottom: '75%',
                    background: 'linear-gradient(90deg, #f0faf5 25%, #e4f5ed 50%, #f0faf5 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
                  }} />
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ height: 13, background: '#e8f0eb', borderRadius: 3, marginBottom: 7, width: '70%' }} />
                    <div style={{ height: 11, background: '#e8f0eb', borderRadius: 3, width: '45%' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', paddingTop: 80, color: '#9ca3af', fontSize: 14 }}>
              {searchQuery ? `No products matching "${searchQuery}".` : 'This supplier has no products yet.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))', gap: 14 }}>
              {products.map(p => (
                <CatalogueProductCard
                  key={p.id}
                  product={p}
                  selected={selectedIds.has(p.id)}
                  onToggle={toggleSelect}
                  onRequestPcf={openPcfRequestModal}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '6px 14px',
                  border: '1px solid #d4ede2',
                  borderRadius: 4,
                  background: page === 1 ? '#f5faf7' : '#ffffff',
                  color: page === 1 ? '#9ca3af' : '#052b16',
                  cursor: page === 1 ? 'default' : 'pointer',
                  fontSize: 13,
                  fontFamily: "'Raleway', sans-serif",
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: 13, color: '#4a5568' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  padding: '6px 14px',
                  border: '1px solid #d4ede2',
                  borderRadius: 4,
                  background: page === totalPages ? '#f5faf7' : '#ffffff',
                  color: page === totalPages ? '#9ca3af' : '#052b16',
                  cursor: page === totalPages ? 'default' : 'pointer',
                  fontSize: 13,
                  fontFamily: "'Raleway', sans-serif",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #e8f0eb',
          padding: '10px 28px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 10,
          marginBottom: 20,
          fontSize: 12,
          color: '#9ca3af',
          flexShrink: 0,
          background: '#fafcfb',
        }}>
          <span>
            {!loading && (
              <>
                Showing {products.length} of {totalCount.toLocaleString()}
                {' · '}
                <strong style={{ color: '#052b16' }}>{pcfCount} with PCF</strong>
                {' on this page'}
              </>
            )}
          </span>
          <span>
            Powered by <strong style={{ color: '#052b16' }}>Zilch</strong>
          </span>
        </div>
      </div>

      {/* ── Request PCF modal ─────────────────────────────────────── */}
      {pcfRequestProduct && (
        <div
          onClick={closePcfRequestModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(32, 33, 36, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: 10,
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
              overflow: 'hidden',
              fontFamily: "'Raleway', 'SF Pro Display', sans-serif",
            }}
          >
            <div style={{ padding: '20px 24px 0' }}>
              <div style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#9ca3af',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Request PCF
              </div>
              <h2 style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 500,
                color: '#052b16',
                letterSpacing: '-0.2px',
                lineHeight: 1.25,
              }}>
                Request PCF data from {supplier?.name || 'the supplier'}
              </h2>
              <p style={{
                margin: '8px 0 0',
                fontSize: 13,
                color: '#6b7280',
                lineHeight: 1.5,
              }}>
                We'll email <strong style={{ color: '#052b16' }}>{supplier?.name || 'the supplier'}</strong> on your behalf to request PCF data for{' '}
                <strong style={{ color: '#052b16' }}>{pcfRequestProduct?.name}</strong>.
                Once they complete the assessment, you'll be able to use the data directly in your catalogue.
              </p>
            </div>

            {pcfSuccess ? (
              <div style={{ padding: '20px 24px 24px' }}>
                <div style={{
                  padding: '12px 14px',
                  background: '#ecfdf5',
                  border: '1px solid #6ee7b7',
                  borderRadius: 6,
                  color: '#065f46',
                  fontSize: 13,
                  lineHeight: 1.5,
                }}>
                  {pcfSuccess}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={closePcfRequestModal}
                    style={{
                      background: '#06a457',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '9px 18px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitPcfRequest} style={{ padding: '20px 24px 24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#052b16',
                  marginBottom: 6,
                  letterSpacing: '0.02em',
                }}>
                  Quantity you'll be selling <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  required
                  value={pcfQuantity}
                  onChange={e => setPcfQuantity(e.target.value)}
                  placeholder="e.g. 1000"
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    fontSize: 14,
                    border: '1px solid #d4ede2',
                    borderRadius: 5,
                    outline: 'none',
                    fontFamily: "'Raleway', sans-serif",
                    color: '#052b16',
                    background: '#ffffff',
                    boxSizing: 'border-box',
                  }}
                />

                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#052b16',
                  margin: '16px 0 6px',
                  letterSpacing: '0.02em',
                }}>
                  Reason for the request <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  required
                  value={pcfReason}
                  onChange={e => setPcfReason(e.target.value)}
                  placeholder="e.g. Submitting a bid to the Australian Open and need a verified PCF for this product."
                  rows={4}
                  maxLength={4000}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 14,
                    border: '1px solid #d4ede2',
                    borderRadius: 5,
                    outline: 'none',
                    fontFamily: "'Raleway', sans-serif",
                    color: '#052b16',
                    background: '#ffffff',
                    resize: 'vertical',
                    minHeight: 90,
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>
                  {pcfReason.length}/4000
                </div>

                {pcfRequestProduct?.already_linked ? (
                  <div style={{
                    marginTop: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12,
                    color: '#06a457',
                    fontWeight: 600,
                  }}>
                    <svg width="13" height="13" viewBox="0 0 11 11" fill="none">
                      <path d="M1.5 5.5l3 3 5-5" stroke="#06a457" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Already in your catalogue — the PCF will appear here automatically.
                  </div>
                ) : (
                  <label style={{
                    marginTop: 16,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '12px 14px',
                    background: '#f9fafa',
                    border: '1px solid #e8f0eb',
                    borderRadius: 6,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}>
                    <input
                      type="checkbox"
                      checked={pcfAddToCatalogue}
                      onChange={e => setPcfAddToCatalogue(e.target.checked)}
                      style={{ cursor: 'pointer', marginTop: 2 }}
                    />
                    <span style={{ fontSize: 13, color: '#052b16', lineHeight: 1.45 }}>
                      <strong style={{ fontWeight: 600 }}>Add this product to my catalogue now</strong>
                      <span style={{ display: 'block', color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                        We'll link it so the PCF is ready in your catalogue the moment {supplier?.name || 'the supplier'} completes the assessment.
                      </span>
                    </span>
                  </label>
                )}

                {pcfError && (
                  <div style={{
                    marginTop: 12,
                    padding: '8px 12px',
                    background: '#fff5f5',
                    border: '1px solid #fca5a5',
                    borderRadius: 4,
                    color: '#dc2626',
                    fontSize: 13,
                  }}>
                    {pcfError}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                  <button
                    type="button"
                    onClick={closePcfRequestModal}
                    disabled={pcfSubmitting}
                    style={{
                      background: '#ffffff',
                      color: '#4a5568',
                      border: '1px solid #d4ede2',
                      borderRadius: 6,
                      padding: '9px 16px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: pcfSubmitting ? 'default' : 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={pcfSubmitting}
                    style={{
                      background: pcfSubmitting ? '#d4ede2' : '#06a457',
                      color: pcfSubmitting ? '#9ca3af' : '#ffffff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '9px 18px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: pcfSubmitting ? 'default' : 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                    }}
                  >
                    {pcfSubmitting ? 'Sending…' : 'Send request →'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Bulk pull modal ─────────────────────────── */}
      {bulkOpen && (
        <div
          onClick={closeBulkModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(32, 33, 36, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: 10,
              width: '100%',
              maxWidth: 520,
              boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
              overflow: 'hidden',
              fontFamily: "'Raleway', 'SF Pro Display', sans-serif",
            }}
          >
            <div style={{ padding: '20px 24px 0' }}>
              <div style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#9ca3af',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Pull in bulk
              </div>
              <h2 style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 500,
                color: '#052b16',
                letterSpacing: '-0.2px',
                lineHeight: 1.25,
              }}>
                Add {supplier?.name || 'this supplier'}’s products to your catalogue
              </h2>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                Pulled products never use your assessment allowance. They arrive with whatever
                PCF the supplier has published, and any PCF completed later appears automatically.
              </p>
            </div>

            {bulkResult ? (
              <div style={{ padding: '20px 24px 24px' }}>
                <div style={{
                  padding: '12px 14px',
                  background: '#ecfdf5',
                  border: '1px solid #6ee7b7',
                  borderRadius: 6,
                  color: '#065f46',
                  fontSize: 13,
                  lineHeight: 1.5,
                }}>
                  {bulkResult.linked > 0
                    ? `Pulled ${bulkResult.linked.toLocaleString()} product${bulkResult.linked === 1 ? '' : 's'} into your catalogue.`
                    : 'Nothing new to pull — those products were already in your catalogue.'}
                  {bulkResult.already_linked > 0 && bulkResult.linked > 0 &&
                    ` ${bulkResult.already_linked.toLocaleString()} were already there.`}
                  {bulkResult.remaining > 0 &&
                    ` ${bulkResult.remaining.toLocaleString()} more matched than we pull at once (${bulkResult.max_per_pull.toLocaleString()} max) — run it again to finish.`}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  {bulkResult.remaining > 0 && (
                    <button
                      type="button"
                      onClick={() => { setBulkResult(null); loadBulkPreview(bulkScope === 'filtered'); }}
                      style={{
                        background: '#ffffff',
                        color: '#06a457',
                        border: '1.5px solid #06a457',
                        borderRadius: 6,
                        padding: '9px 16px',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer',
                        fontFamily: "'Raleway', sans-serif",
                        letterSpacing: '0.03em',
                      }}
                    >
                      Pull the rest
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={closeBulkModal}
                    style={{
                      background: '#06a457',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '9px 18px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '18px 24px 24px' }}>
                {hasActiveFilters ? (
                  <>
                    {renderScopeOption(
                      'filtered',
                      filterScopeLabel,
                      'Everything matching the filters you have on now',
                      bulkPreviewLoading ? null : bulkPreview?.filtered || null
                    )}
                    {renderScopeOption(
                      'catalogue',
                      `${supplier?.name || 'This supplier'}’s entire catalogue`,
                      'Every active product they publish, ignoring your filters',
                      bulkPreviewLoading ? null : bulkPreview?.catalogue || null
                    )}
                  </>
                ) : renderScopeSummary(
                  `${supplier?.name || 'This supplier'}’s entire catalogue`,
                  'Every active product they publish',
                  bulkPreviewLoading ? null : bulkPreview?.catalogue || null
                )}

                {/* A filter on the scope above, not another scope — kept unfilled
                    and below a divider so it can't read as a second selection. */}
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop: '1px solid #eef2f0',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}>
                  <input
                    type="checkbox"
                    checked={bulkOnlyPcf}
                    onChange={e => setBulkOnlyPcf(e.target.checked)}
                    style={{ cursor: 'pointer', marginTop: 2 }}
                  />
                  <span style={{ fontSize: 13, color: '#052b16', lineHeight: 1.45 }}>
                    <strong style={{ fontWeight: 600 }}>Only products that already have a PCF</strong>
                    <span style={{ display: 'block', color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                      Skip anything the supplier hasn’t assessed yet. You can request those
                      individually from the catalogue.
                    </span>
                  </span>
                </label>

                {bulkError && (
                  <div style={{
                    marginTop: 12,
                    padding: '8px 12px',
                    background: '#fff5f5',
                    border: '1px solid #fca5a5',
                    borderRadius: 4,
                    color: '#dc2626',
                    fontSize: 13,
                  }}>
                    {bulkError}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 18 }}>
                  <button
                    type="button"
                    onClick={closeBulkModal}
                    disabled={bulkSubmitting}
                    style={{
                      background: '#ffffff',
                      color: '#4a5568',
                      border: '1px solid #d4ede2',
                      borderRadius: 6,
                      padding: '9px 16px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: bulkSubmitting ? 'default' : 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                    }}
                  >
                    Cancel
                  </button>
                  {(() => {
                    // A failed count is recoverable, so the button retries the
                    // count instead of sitting there disabled.
                    const countFailed = !!bulkError && !bulkPreview;
                    const idle = !bulkSubmitting && !bulkPreviewLoading;
                    const enabled = idle && (countFailed || bulkNewCount > 0);
                    return (
                      <button
                        type="button"
                        onClick={countFailed ? () => loadBulkPreview(bulkScope === 'filtered') : submitBulkPull}
                        disabled={!enabled}
                        style={{
                          background: enabled ? '#06a457' : '#d4ede2',
                          color: enabled ? '#ffffff' : '#9ca3af',
                          border: 'none',
                          borderRadius: 6,
                          padding: '9px 18px',
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: enabled ? 'pointer' : 'default',
                          fontFamily: "'Raleway', sans-serif",
                          letterSpacing: '0.03em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {bulkSubmitting
                          ? 'Pulling…'
                          : bulkPreviewLoading
                            ? 'Counting…'
                            : countFailed
                              ? 'Try again'
                              : bulkNewCount === 0
                                ? (bulkScopeTotal === 0 ? 'Nothing to pull' : 'Already in your catalogue')
                                : `Pull ${bulkNewCount.toLocaleString()} product${bulkNewCount === 1 ? '' : 's'} →`}
                      </button>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Ask for footprints (bulk PCF request) ─────────────────── */}
      {askOpen && (
        <div
          onClick={closeAskModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(32, 33, 36, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: 10,
              width: '100%',
              maxWidth: 500,
              boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
              overflow: 'hidden',
              fontFamily: "'Raleway', 'SF Pro Display', sans-serif",
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ padding: '20px 24px 0' }}>
              <div style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#9ca3af',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Request footprints
              </div>
              <h2 style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 500,
                color: '#052b16',
                letterSpacing: '-0.2px',
                lineHeight: 1.25,
              }}>
                Ask {supplier?.name || 'this supplier'} for carbon footprints
              </h2>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                One email covering <strong style={{ color: '#052b16' }}>{askScopeLabel()}</strong> — not one
                message per product. They'll see exactly which products their distributors need data for.
              </p>
            </div>

            {askResult ? (
              <div style={{ padding: '20px 24px 24px' }}>
                <div style={{
                  padding: '12px 14px',
                  background: '#ecfdf5',
                  border: '1px solid #6ee7b7',
                  borderRadius: 6,
                  color: '#065f46',
                  fontSize: 13,
                  lineHeight: 1.55,
                }}>
                  {askResult.created > 0 ? (
                    <>
                      Requested footprints for <strong>{(askResult.created || 0).toLocaleString()}</strong> product
                      {askResult.created === 1 ? '' : 's'}.{' '}
                      {askResult.email_sent
                        ? `${supplier?.name || 'The supplier'} has been emailed and will reply directly to you.`
                        : askResult.supplier_has_email === false
                          ? `${supplier?.name || 'This supplier'} hasn't claimed their Zilch account yet, so we couldn't email them — we'll surface it next time they sign in.`
                          : `We'll try the email again shortly.`}
                      {askResult.capped && (
                        <>
                          {' '}This send was capped at {(askResult.limit || 0).toLocaleString()} products; run it
                          again to continue through the rest.
                        </>
                      )}
                    </>
                  ) : (
                    <>Nothing new to request — every product here either has a footprint already or has been requested before.</>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={closeAskModal}
                    style={{
                      background: '#06a457',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '9px 18px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitAsk} style={{ padding: '20px 24px 24px' }}>
                {/* What the request actually covers, before they commit to it */}
                <div style={{
                  padding: '12px 14px',
                  background: '#f9fafa',
                  border: '1px solid #e8f0eb',
                  borderRadius: 6,
                  marginBottom: 18,
                  fontSize: 13,
                  color: '#052b16',
                  lineHeight: 1.6,
                }}>
                  {askPreviewLoading ? (
                    <span style={{ color: '#9ca3af' }}>Counting products…</span>
                  ) : askPreview ? (
                    <>
                      <div>
                        <strong style={{ fontWeight: 700 }}>
                          {Math.min(askPreview.requestable || 0, askPreview.limit || 0).toLocaleString()}
                        </strong>
                        {' products will be requested'}
                      </div>
                      {(askPreview.with_footprint > 0 || askPreview.already_requested > 0) && (
                        <div style={{ color: '#6b7280', fontSize: 12.5, marginTop: 3 }}>
                          Skipping{' '}
                          {askPreview.with_footprint > 0 && `${askPreview.with_footprint.toLocaleString()} that already have a footprint`}
                          {askPreview.with_footprint > 0 && askPreview.already_requested > 0 && ' and '}
                          {askPreview.already_requested > 0 && `${askPreview.already_requested.toLocaleString()} you've already requested`}
                          .
                        </div>
                      )}
                      {askPreview.capped && (
                        <div style={{ color: '#6b7280', fontSize: 12.5, marginTop: 3 }}>
                          {(askPreview.requestable || 0).toLocaleString()} match in total — capped at{' '}
                          {(askPreview.limit || 0).toLocaleString()} per request so the supplier gets a list they
                          can actually act on. Run it again for the rest.
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>Couldn't count the products in scope.</span>
                  )}
                </div>

                {/* No per-product quantity here: asking "how many will you
                    sell, per product" across a whole catalogue has no honest
                    answer. The reason is the useful signal at this scale. */}
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#052b16',
                  marginBottom: 6,
                  letterSpacing: '0.02em',
                }}>
                  Reason for the request <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  required
                  value={askReason}
                  onChange={e => setAskReason(e.target.value)}
                  placeholder="e.g. Our clients now ask for per-product emissions on every tender, and we'd rather keep buying from you than switch to a supplier who has the data."
                  rows={4}
                  maxLength={4000}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 14,
                    border: '1px solid #d4ede2',
                    borderRadius: 5,
                    outline: 'none',
                    fontFamily: "'Raleway', sans-serif",
                    color: '#052b16',
                    background: '#ffffff',
                    resize: 'vertical',
                    minHeight: 90,
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>
                  {askReason.length}/4000
                </div>

                {askError && (
                  <div style={{
                    marginTop: 12,
                    padding: '8px 12px',
                    background: '#fff5f5',
                    border: '1px solid #fca5a5',
                    borderRadius: 4,
                    color: '#dc2626',
                    fontSize: 13,
                  }}>
                    {askError}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                  <button
                    type="button"
                    onClick={closeAskModal}
                    disabled={askSubmitting}
                    style={{
                      background: '#ffffff',
                      color: '#4a5568',
                      border: '1px solid #d4ede2',
                      borderRadius: 6,
                      padding: '9px 16px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: askSubmitting ? 'default' : 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={askSubmitting}
                    style={{
                      background: '#000000',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '9px 18px',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: askSubmitting ? 'default' : 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                      letterSpacing: '0.03em',
                      opacity: askSubmitting ? 0.75 : 1,
                    }}
                  >
                    {askSubmitting ? 'Sending…' : 'Send request'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
